import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";

// Filtre « Produits / Catégories » d'Activité › Résumé.
//   GET /activite/product_stats   stats nettes des remises (backend clicoo
//                                 ProductStatsService) pour la sélection
//   GET /products?name=…          recherche produit (nom ou code-barres)
//   GET /categories               toutes les catégories
//
// Une sélection est une liste [{type: "product" | "category", id, name}].

export const FILTER_STORAGE_KEY = "activity_productFilter";

const headers = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account?.id, Accept: "application/json"};
};

export const selectionIds = (selection = []) => ({
    productIds: selection.filter((s) => s.type === "product").map((s) => s.id),
    categoryIds: selection.filter((s) => s.type === "category").map((s) => s.id),
});

export const buildProductStatsQuery = ({range, startTime, endTime, selection}) => {
    const params = new URLSearchParams({
        startDate: toLocalDateStr(range.startDate),
        endDate: toLocalDateStr(range.endDate),
        startTime: startTime || "00:00",
        endTime: endTime || "23:59",
    });
    const {productIds, categoryIds} = selectionIds(selection);
    productIds.forEach((id) => params.append("productIds[]", id));
    categoryIds.forEach((id) => params.append("categoryIds[]", id));
    return params.toString();
};

export const fetchProductStats = async (args) => {
    const {data} = await api.get(`/activite/product_stats?${buildProductStatsQuery(args)}`, {headers: await headers()});
    return data;
};

const listOf = (data) => (Array.isArray(data) ? data : data?.data || data?.["hydra:member"] || []);

export const searchProducts = async (term = "") => {
    const params = new URLSearchParams({page: "1", itemsPerPage: "40"});
    if (term.trim()) params.set("name", term.trim());
    const {data} = await api.get(`/products?${params.toString()}`, {headers: await headers()});
    return listOf(data).filter((p) => p?.id).map((p) => ({
        id: p.id,
        name: p.name || "Produit",
        categoryName: p.category?.name || p.categoryName || "",
        image: p.image || null,
        variantsCount: Array.isArray(p.variants) ? p.variants.length : 0,
    }));
};

export const fetchCategories = async () => {
    const {data} = await api.get("/categories?page=1&itemsPerPage=1000", {headers: await headers()});
    return listOf(data).filter((c) => c?.id).map((c) => ({
        id: c.id,
        name: c.name || "Catégorie",
        color: c.color || null,
    })).sort((a, b) => a.name.localeCompare(b.name, "fr"));
};

// ── Persistance (par navigateur, comme la période) ──────────────────────────

export const readStoredSelection = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || "[]");
        return Array.isArray(parsed)
            ? parsed.filter((s) => s && (s.type === "product" || s.type === "category") && s.id)
            : [];
    } catch {
        return [];
    }
};

export const storeSelection = (selection) => {
    try {
        if (selection.length) localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(selection));
        else localStorage.removeItem(FILTER_STORAGE_KEY);
    } catch {
        // stockage indisponible : le filtre ne survivra pas au rechargement
    }
};

// ── Export CSV ──────────────────────────────────────────────────────────────

const csvCell = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const num = (v) => (Number(v) || 0).toFixed(2).replace(".", ",");

// Séparateur « ; » et virgule décimale : s'ouvre directement dans Excel FR.
export const statsToCsv = (stats, {startDate, endDate} = {}) => {
    const rows = [
        [`Période`, `${startDate || ""} → ${endDate || ""}`],
        [],
        ["Produit", "Catégorie", "Quantité", "CA brut", "Remises", "CA net", "Tickets", "Part (%)"],
        ...(stats?.products || []).map((p) => [
            p.name, p.category_name || "", num(p.quantity), num(p.gross), num(p.discount), num(p.net), p.tickets, num(p.share),
        ]),
        [],
        ["Catégorie", "Produits", "Quantité", "CA brut", "Remises", "CA net", "Tickets", "Part (%)"],
        ...(stats?.categories || []).map((c) => [
            c.name, c.products_count, num(c.quantity), num(c.gross), num(c.discount), num(c.net), c.tickets, num(c.share),
        ]),
        [],
        ["Total", "", num(stats?.summary?.quantity), num(stats?.summary?.gross), num(stats?.summary?.discount),
            num(stats?.summary?.net), stats?.summary?.tickets ?? 0, num(stats?.summary?.share_of_revenue)],
    ];
    return `﻿${rows.map((r) => r.map(csvCell).join(";")).join("\n")}`;
};

export const downloadCsv = (content, filename) => {
    const blob = new Blob([content], {type: "text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
